/**
 * RoadmapBadges.tsx — status/effort badges shared by the roadmap pages.
 * Colors follow design-tokens.mdc §7: success token for done/complete,
 * amber = warning-ish states, sky = informational, destructive for blocked.
 */

import { Badge } from '@/components/ui/badge';
import type {
  FeatureRequestStatus,
  FeatureRequestType,
  RoadmapMilestoneStatus,
  TicketOrigin,
} from '@/api/endpoints/roadmap';
import { MILESTONE_STATUS_LABELS, TICKET_ORIGIN_LABELS } from '@/api/endpoints/roadmap';
import { TICKET_STATUS_DEFINITIONS, TICKET_STATUS_LABELS } from '../lib/ticket-filters';

export const REQUEST_STATUS_CLASSES: Record<FeatureRequestStatus, string> = {
  new: 'bg-sky-100 text-sky-600 dark:bg-sky-950 dark:text-sky-400',
  triaged: 'bg-primary/10 text-primary',
  planning: 'bg-muted text-muted-foreground',
  development: 'bg-primary/10 text-primary',
  qa: 'bg-sky-100 text-sky-600 dark:bg-sky-950 dark:text-sky-400',
  acceptance_testing: 'bg-primary/10 text-primary-text',
  ready_for_release: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400',
  complete: 'bg-success/10 text-success',
  blocked: 'bg-destructive/10 text-destructive',
  // Their side, not ours (R5.19) — warning-ish, distinct from blocked's red.
  waiting_on_submitter: 'bg-amber-100 text-amber-600 dark:bg-amber-950 dark:text-amber-400',
  declined: 'bg-muted text-muted-foreground',
  duplicate: 'bg-muted text-muted-foreground',
};

/** Bug reads as a problem (destructive); feature as planned work; feedback as informational; maintenance as upkeep. */
const REQUEST_TYPE_CLASSES: Record<FeatureRequestType, string> = {
  feature: 'bg-primary/10 text-primary',
  bug: 'bg-destructive/10 text-destructive-text',
  feedback: 'bg-amber-100 text-amber-600 dark:bg-amber-950 dark:text-amber-400',
  maintenance: 'bg-info-bg text-info',
};

const REQUEST_TYPE_LABELS: Record<FeatureRequestType, string> = {
  feature: 'Feature',
  bug: 'Bug',
  feedback: 'Feedback',
  maintenance: 'Maintenance',
};

const MILESTONE_STATUS_CLASSES: Record<RoadmapMilestoneStatus, string> = {
  planned: 'bg-muted text-muted-foreground',
  'needs-grooming': 'bg-info-bg text-info',
  'on-track': 'bg-success/10 text-success',
  'at-risk': 'bg-amber-100 text-amber-600 dark:bg-amber-950 dark:text-amber-400',
  done: 'bg-success/10 text-success',
  slipped: 'bg-destructive/10 text-destructive-text',
};

/** Board-row status chip — same vocabulary as the ticket. */
export function FeatureStatusBadge({ status }: { status: FeatureRequestStatus }) {
  return (
    <Badge
      variant="outline"
      title={TICKET_STATUS_DEFINITIONS[status]}
      className={`border-transparent ${REQUEST_STATUS_CLASSES[status]}`}
    >
      {TICKET_STATUS_LABELS[status]}
    </Badge>
  );
}

/** Ticket status chip — environment + owner on hover (R5.16). */
export function RequestStatusBadge({ status }: { status: FeatureRequestStatus }) {
  return (
    <Badge
      variant="outline"
      title={TICKET_STATUS_DEFINITIONS[status]}
      className={`border-transparent ${REQUEST_STATUS_CLASSES[status]}`}
    >
      {TICKET_STATUS_LABELS[status]}
    </Badge>
  );
}

/** Human label for a ticket type; `null` for the unclassified `''`. */
export function ticketTypeLabel(type: FeatureRequestType | ''): string | null {
  return type ? REQUEST_TYPE_LABELS[type] : null;
}

/**
 * Ticket kind badge. Unclassified tickets say "Untyped" as quiet text instead of
 * a chip: it still names the gap, but a backlog full of them doesn't read as a
 * wall of badges competing with the real ones.
 */
export function TicketTypeBadge({ type }: { type: FeatureRequestType | '' }) {
  if (!type) {
    return <span className="text-xs text-muted-foreground">Untyped</span>;
  }
  return (
    <Badge variant="outline" className={`border-transparent ${REQUEST_TYPE_CLASSES[type]}`}>
      {REQUEST_TYPE_LABELS[type]}
    </Badge>
  );
}

/** Program-milestone status chip. */
export function MilestoneStatusBadge({ status }: { status: RoadmapMilestoneStatus }) {
  return (
    <Badge
      variant="outline"
      title={status === 'needs-grooming' ? 'Not enough context yet to estimate the dates' : undefined}
      className={`border-transparent ${MILESTONE_STATUS_CLASSES[status]}`}
    >
      {MILESTONE_STATUS_LABELS[status]}
    </Badge>
  );
}

/** Product tag: Tools, CRM, TruSpace, or CAD. Categorical — not a status. */
const ORIGIN_CLASSES: Record<TicketOrigin, string> = {
  tools: 'bg-muted text-muted-foreground',
  crm: 'bg-info-bg text-info',
  truspace: 'bg-primary/10 text-primary-text',
  cad: 'bg-secondary text-secondary-foreground',
};

export function OriginBadge({ origin }: { origin: TicketOrigin | null | undefined }) {
  if (!origin || !(origin in TICKET_ORIGIN_LABELS)) return null;
  return (
    <Badge variant="outline" className={`border-transparent ${ORIGIN_CLASSES[origin]}`}>
      {TICKET_ORIGIN_LABELS[origin]}
    </Badge>
  );
}
